import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { organizationsApi, formatApiError, extractApiErrorCode } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    User, Building, Save, Loader2, Users, UserPlus, AlertTriangle, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

export default function SettingsPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [orgForm, setOrgForm] = useState({ name: '', industry: '' });
    const [usage, setUsage] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('recruiter');
    const [inviting, setInviting] = useState(false);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const canManageMembers = user?.role === 'admin' || user?.role === 'manager';

    useEffect(() => {
        loadOrganization();
        if (canManageMembers) {
            loadUsage();
            loadMembers();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadMembers = async () => {
        setMembersLoading(true);
        try {
            const res = await organizationsApi.getMembers();
            setMembers(Array.isArray(res.data) ? res.data : []);
        } catch (_) {
            /* silent — the counter still renders */
        } finally {
            setMembersLoading(false);
        }
    };

    const handleDeleteMember = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await organizationsApi.removeMember(deleteTarget.id);
            toast.success(t('settings.member_deleted_success'));
            setDeleteTarget(null);
            await loadUsage();
            await loadMembers();
        } catch (error) {
            const code = extractApiErrorCode(error);
            const msg = code
                ? t(`auth.errors.${code}`, { defaultValue: formatApiError(error) })
                : formatApiError(error);
            toast.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    const loadOrganization = async () => {
        try {
            const response = await organizationsApi.getCurrent();
            setOrganization(response.data);
            setOrgForm({ name: response.data.name || '', industry: response.data.industry || '' });
        } catch (error) {
            // No organization
        } finally {
            setLoading(false);
        }
    };

    const loadUsage = async () => {
        try {
            const response = await organizationsApi.getMembersUsage();
            setUsage(response.data);
        } catch (error) {
            // ignore
        }
    };

    const handleSaveOrg = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await organizationsApi.update(orgForm);
            toast.success(t('settings.updated_success'));
            loadOrganization();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setSaving(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail) return;
        setInviting(true);
        try {
            await organizationsApi.invite(inviteEmail, inviteRole);
            toast.success(t('settings.invite_sent_success'));
            setInviteEmail('');
            setInviteRole('recruiter');
            loadUsage();
            loadMembers();
        } catch (error) {
            const code = extractApiErrorCode(error);
            const msg = code
                ? t(`auth.errors.${code}`, { defaultValue: formatApiError(error) })
                : formatApiError(error);
            toast.error(msg);
        } finally {
            setInviting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    const limitReached = usage?.users_limit_reached === true;

    return (
        <div className="max-w-2xl mx-auto space-y-8" data-testid="settings-page">
            <div>
                <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('settings.page_title')}</h1>
                <p className="text-[#4B5563]">{t('settings.subtitle')}</p>
            </div>

            {/* User info */}
            <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5 text-[#002FA7]" />
                    </div>
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('settings.personal_info')}
                    </h2>
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-[#9CA3AF]">{t('settings.name')}</p>
                            <p className="font-medium text-[#0A0A0A]">{user?.name}</p>
                        </div>
                        <div>
                            <p className="text-sm text-[#9CA3AF]">{t('settings.email')}</p>
                            <p className="font-medium text-[#0A0A0A]">{user?.email}</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-[#9CA3AF]">{t('settings.role')}</p>
                        <p className="font-medium text-[#0A0A0A] capitalize">{user?.role}</p>
                    </div>
                </div>
            </div>

            {/* Organization */}
            {organization && (
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-[#D1FAE5] rounded-lg flex items-center justify-center">
                            <Building className="w-5 h-5 text-[#10B981]" />
                        </div>
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('settings.organization_title')}
                        </h2>
                    </div>

                    <form onSubmit={handleSaveOrg} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="orgName">{t('settings.organization_name_label')}</Label>
                            <Input
                                id="orgName"
                                value={orgForm.name}
                                onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                                placeholder={t('settings.organization_name_placeholder')}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="industry">{t('settings.industry_label')}</Label>
                            <Input
                                id="industry"
                                value={orgForm.industry}
                                onChange={(e) => setOrgForm({ ...orgForm, industry: e.target.value })}
                                placeholder={t('settings.industry_placeholder')}
                            />
                        </div>
                        <Button
                            type="submit"
                            className="bg-[#002FA7] hover:bg-[#002480] text-white"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('common.saving')}
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    {t('common.save')}
                                </>
                            )}
                        </Button>
                    </form>
                </div>
            )}

            {/* Members & Invite — admin/manager only */}
            {canManageMembers && (
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]" data-testid="members-section">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-[#EDE9FE] rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#7C3AED]" />
                        </div>
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('settings.members_title')}
                        </h2>
                    </div>
                    <p className="text-sm text-[#4B5563] mb-4">{t('settings.members_subtitle')}</p>

                    {usage && (
                        <div
                            data-testid="members-usage-counter"
                            className={`mb-4 px-4 py-3 rounded-lg border text-sm flex items-center justify-between gap-3 ${
                                limitReached
                                    ? 'border-[#F59E0B] bg-[#FFFBEB] text-[#78350F]'
                                    : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#0A0A0A]'
                            }`}
                        >
                            <span className="font-medium">
                                {t('settings.members_usage', {
                                    count: usage.members_count,
                                    max: usage.max_users,
                                })}
                            </span>
                            {limitReached && (
                                <span className="flex items-center gap-1 text-xs font-semibold">
                                    <AlertTriangle className="w-4 h-4" />
                                    {t('settings.members_limit_reached')}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Members list */}
                    <div className="mb-6" data-testid="members-list">
                        <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3">
                            {t('settings.members_list_title', { count: members.length })}
                        </h3>
                        {membersLoading && members.length === 0 ? (
                            <div className="flex items-center gap-2 text-sm text-[#6B7280] py-4">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('common.loading')}
                            </div>
                        ) : members.length === 0 ? (
                            <p className="text-sm text-[#6B7280] py-4">{t('settings.members_list_empty')}</p>
                        ) : (
                            <ul className="divide-y divide-[#E5E7EB] border border-[#E5E7EB] rounded-lg overflow-hidden">
                                {members.map((m) => {
                                    const isSelf = m.id === user?.id;
                                    return (
                                        <li
                                            key={m.id}
                                            data-testid={`member-row-${m.email}`}
                                            className="flex items-center justify-between gap-3 px-4 py-3 bg-white hover:bg-[#F9FAFB]"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-full bg-[#EEF2FF] flex items-center justify-center text-[#002FA7] text-sm font-semibold">
                                                    {(m.name || m.email || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-[#0A0A0A] truncate">
                                                        {m.name || m.email}
                                                        {isSelf && (
                                                            <span className="ml-2 text-xs text-[#6B7280]">
                                                                ({t('settings.members_list_you')})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-[#6B7280] truncate">{m.email}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                <span className="text-xs font-medium text-[#4B5563] uppercase tracking-wide">
                                                    {t(`settings.role_${m.role}`, { defaultValue: m.role })}
                                                </span>
                                                {!isSelf && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteTarget(m)}
                                                        data-testid={`delete-member-btn-${m.email}`}
                                                        className="text-[#DC2626] hover:text-[#B91C1C] hover:bg-[#FEE2E2]"
                                                        title={t('settings.member_delete')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <form onSubmit={handleInvite} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="inviteEmail">{t('settings.invite_email_label')}</Label>
                                <Input
                                    id="inviteEmail"
                                    data-testid="invite-email-input"
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder={t('settings.invite_email_placeholder')}
                                    disabled={limitReached}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="inviteRole">{t('settings.invite_role_label')}</Label>
                                <Select
                                    value={inviteRole}
                                    onValueChange={setInviteRole}
                                    disabled={limitReached}
                                >
                                    <SelectTrigger id="inviteRole" data-testid="invite-role-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="recruiter">{t('settings.invite_role_recruiter')}</SelectItem>
                                        <SelectItem value="manager">{t('settings.invite_role_manager')}</SelectItem>
                                        <SelectItem value="billing_admin">{t('settings.invite_role_billing')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            data-testid="invite-user-button"
                            className="bg-[#002FA7] hover:bg-[#002480] text-white"
                            disabled={limitReached || inviting || !inviteEmail}
                            title={limitReached ? t('settings.invite_button_disabled_tooltip') : undefined}
                        >
                            {inviting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('common.saving')}
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    {t('settings.invite_button')}
                                </>
                            )}
                        </Button>
                    </form>
                </div>
            )}

            {/* Delete Member Confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
                <AlertDialogContent data-testid="delete-member-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('settings.member_delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('settings.member_delete_desc', {
                                name: deleteTarget?.name || deleteTarget?.email,
                                email: deleteTarget?.email,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteMember}
                            disabled={deleting}
                            className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                            data-testid="confirm-delete-member-btn"
                        >
                            {deleting ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.saving')}</>
                            ) : t('common.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
